import { bound } from "@frui.ts/helpers";
import { BusyWatcher, Router, watchBusy } from "@frui.ts/screens";
import { Modules } from "config";
import LocalizationService from "services/localizationService";
import { computed, observable } from "mobx";
import OutgoingPayGateRepository from "repositories/outgoingPayGateRepository";
import type PayGateSenderBankAccount from "entities/payGateSenderBankAccountResponse";
import EnumService from "services/enumService";
import type { interfaces } from "inversify";
import DetailViewModel from "../../detailViewModel";
import type OutgoingPayGatePaymentStatus from "entities/outgoingPayGatePaymentStatus";
import type { IWithPrevNextNavigationViewModel } from "../../types";
import PagingManagerViewModel from "../../pagingManagerViewModel";
import type OutgoingPayGatePaymentDetailResponse from "entities/outgoingPayGatePaymentDetailResponse";
import type OutgoingPayGateTransactionDetailResponse from "entities/outgoingPayGateTransactionDetailResponse";
import PriceListService from "services/priceListService";
import { translateChargeTypeByPriceListItem } from "utils/chargeType";

@Router.registerRoute({ name: Router.Self, route: ":paymentId" })
export default class CardGatewayDetailViewModel
  extends DetailViewModel<OutgoingPayGatePaymentDetailResponse>
  implements IWithPrevNextNavigationViewModel
{
  protected readonly modelName = "outgoingPayGatePayment";
  @observable busyWatcher: BusyWatcher = new BusyWatcher();
  @observable protected itemId: string;

  @computed
  get isBusy() {
    return this.busyWatcher.isBusy;
  }

  constructor(
    itemId: string,
    private pagingManager: PagingManagerViewModel,
    private repository: OutgoingPayGateRepository,
    public localizationService: LocalizationService,
    private enumService: EnumService,
    private priceListService: PriceListService
  ) {
    super(itemId);
  }

  @watchBusy
  protected loadDetail(): Promise<OutgoingPayGatePaymentDetailResponse> {
    return this.repository.getOutgoingPayGatePayment(this.itemId);
  }

  @bound
  close() {
    this.requestClose();
  }

  @computed
  get hasPrevious() {
    return this.pagingManager.hasPrevious;
  }

  @computed
  get hasNext() {
    return this.pagingManager.hasNext;
  }

  @bound
  @watchBusy
  async onPreviousClick() {
    const itemId = await this.pagingManager.onPreviousClick();

    if (itemId) {
      this.itemId = itemId;
      this.navigationName = `${this.itemId}`;
      this.notifyNavigationChanged();

      await this.onInitialize();
    }
  }

  @bound
  @watchBusy
  async onNextClick() {
    const itemId = await this.pagingManager.onNextClick();
    if (itemId) {
      this.itemId = itemId;
      this.navigationName = `${this.itemId}`;
      this.notifyNavigationChanged();

      await this.onInitialize();
    }
  }

  countryCode(countryId: string) {
    return this.enumService.value("countries", countryId)?.code ?? "";
  }

  chargeType(priceListItemId?: string) {
    return translateChargeTypeByPriceListItem(this.priceListService, this.enumService, priceListItemId);
  }

  translate(code: string, size?: number) {
    return this.localizationService.translateModule(Modules.finance, `outgoingPayments.${code}`, size);
  }

  translateModelAttribute(model: "payGateSenderBankAccount", attribute: keyof PayGateSenderBankAccount, size?: number): string;
  translateModelAttribute(
    model: "outgoingPayGatePayment",
    attribute: keyof OutgoingPayGateTransactionDetailResponse,
    size?: number
  ): string;
  translateModelAttribute(model: string, attribute: string, size?: number) {
    return this.localizationService.translateAttribute(model, attribute, size);
  }

  translateOutgoingPayGatePaymentStatus(status: OutgoingPayGatePaymentStatus) {
    return this.localizationService.translateEnum("outgoingPayGatePaymentStatus", status);
  }

  static Factory({ container }: interfaces.Context) {
    return (itemId: string, pagingManager: PagingManagerViewModel) =>
      new CardGatewayDetailViewModel(
        itemId,
        pagingManager,
        container.get(OutgoingPayGateRepository),
        container.get(LocalizationService),
        container.get(EnumService),
        container.get(PriceListService)
      );
  }
}
